// -----------------------------------------------------------------------------
// <MascotFX /> — canvas-based volumetric FX engine for the Splash mascot.
// -----------------------------------------------------------------------------
// A single full-viewport <canvas> runs a requestAnimationFrame loop that:
//   1. Tracks the REAL on-screen position of the mascot figure every frame by
//      reading its DOM rect (so the smoke follows him perfectly through any
//      spring / tween / keyframe motion — no coupling to animation state).
//   2. Emits volumetric smoke puffs proportional to flight speed. Every puff
//      carries a z-depth value in [0..1] that drives its size, opacity,
//      parallax speed and draw order — far puffs are small, dim, slow;
//      near puffs are large, bright, fast. Depth-sorted each frame.
//   3. Renders dynamic shadows projected from a fixed overhead light source.
//      The character AND every smoke puff cast a soft elliptical shadow whose
//      offset is computed from the light direction — so the shadow trail
//      sweeps across the page content naturally as Splash flies.
// Zero React re-renders per frame: everything lives in refs + canvas.
// -----------------------------------------------------------------------------

import React, { useEffect, useRef } from 'react';

const MAX_PUFFS = 220;
const EMIT_SPEED_THRESHOLD = 90;   // px/s — below this, no smoke
const DPR_CAP = 1.75;

function prefersReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function MascotFX({ targetSelector, enabled = true, intensity = 1 }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!enabled || prefersReducedMotion()) return undefined;
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext('2d');

        const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
        let vw = window.innerWidth;
        let vh = window.innerHeight;

        function resize() {
            vw = window.innerWidth;
            vh = window.innerHeight;
            canvas.width = Math.round(vw * dpr);
            canvas.height = Math.round(vh * dpr);
            canvas.style.width = `${vw}px`;
            canvas.style.height = `${vh}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }
        resize();
        window.addEventListener('resize', resize);

        // Light source: high above the top edge, slightly left of center.
        const light = () => ({ x: vw * 0.38, y: -vh * 0.9 });

        const puffs = [];
        let prev = null;          // { x, y, t }
        let velX = 0;
        let velY = 0;             // smoothed velocity px/s
        let emitBudget = 0;
        let raf = 0;
        let lastT = performance.now();

        function spawnPuff(cx, cy, ux, uy, speed) {
            if (puffs.length >= MAX_PUFFS) puffs.shift();
            const z = Math.random();                       // depth 0=far 1=near
            const jitter = () => (Math.random() - 0.5);
            puffs.push({
                x: cx + jitter() * 16,
                y: cy + jitter() * 16,
                vx: -ux * speed * (0.10 + z * 0.07) + jitter() * 26,
                vy: -uy * speed * (0.10 + z * 0.07) + jitter() * 26 - 12,
                z,
                r: 7 + Math.random() * 9,
                growth: 26 + Math.random() * 30,           // px/s radius growth
                life: 0,
                maxLife: 0.65 + Math.random() * 0.7,
                phase: Math.random() * Math.PI * 2,
                warm: Math.random() < 0.1,                 // occasional golden puff
            });
        }

        function drawShadow(cx, cy, w, alpha, blurScale) {
            const L = light();
            const dx = cx - L.x;
            const dy = cy - L.y;
            const len = Math.hypot(dx, dy) || 1;
            const dist = 26 + blurScale * 34;
            const sx = cx + (dx / len) * dist;
            const sy = cy + (dy / len) * dist;
            const rw = w * (1 + blurScale * 0.35);
            const rh = rw * 0.38;
            const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, rw);
            g.addColorStop(0, `rgba(10, 22, 40, ${alpha})`);
            g.addColorStop(0.55, `rgba(10, 22, 40, ${alpha * 0.45})`);
            g.addColorStop(1, 'rgba(10, 22, 40, 0)');
            ctx.save();
            ctx.translate(sx, sy);
            ctx.scale(1, rh / rw);
            ctx.translate(-sx, -sy);
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(sx, sy, rw, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }

        function frame(now) {
            raf = requestAnimationFrame(frame);
            const dt = Math.min(0.05, (now - lastT) / 1000);
            lastT = now;

            const el = document.querySelector(targetSelector);
            let center = null;
            let figW = 0;
            let visible = false;
            if (el) {
                const rect = el.getBoundingClientRect();
                if (rect.width > 4 && rect.right > -80 && rect.left < vw + 80) {
                    center = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
                    figW = rect.width;
                    visible = true;
                }
            }

            // ---- velocity from real DOM movement -------------------------
            let speed = 0;
            if (center && prev) {
                const rawVx = (center.x - prev.x) / dt;
                const rawVy = (center.y - prev.y) / dt;
                velX += (rawVx - velX) * 0.35;
                velY += (rawVy - velY) * 0.35;
                speed = Math.hypot(velX, velY);
            }
            prev = center ? { ...center } : null;

            // ---- emission -------------------------------------------------
            if (center && speed > EMIT_SPEED_THRESHOLD) {
                const ux = velX / (speed || 1);
                const uy = velY / (speed || 1);
                emitBudget += Math.min(speed / 260, 4.5) * intensity * dt * 34;
                while (emitBudget >= 1) {
                    emitBudget -= 1;
                    spawnPuff(
                        center.x - ux * figW * 0.34,
                        center.y - uy * figW * 0.34 + figW * 0.08,
                        ux, uy, speed,
                    );
                }
            } else {
                emitBudget = 0;
            }

            // ---- update puffs ---------------------------------------------
            for (let i = puffs.length - 1; i >= 0; i--) {
                const p = puffs[i];
                p.life += dt;
                if (p.life >= p.maxLife) { puffs.splice(i, 1); continue; }
                const par = 0.5 + p.z * 0.65;              // depth parallax
                const t = p.life * 4 + p.phase;
                p.vx += (Math.sin(t) * 26 + Math.sin(t * 2.3 + 1.7) * 14) * dt;
                p.vy += (Math.cos(t * 1.4) * 18) * dt - 34 * dt; // buoyancy
                p.vx *= 1 - 1.6 * dt;                      // drag
                p.vy *= 1 - 1.6 * dt;
                p.x += p.vx * par * dt;
                p.y += p.vy * par * dt;
                p.r += p.growth * dt;
            }

            // ---- render ----------------------------------------------------
            ctx.clearRect(0, 0, vw, vh);
            if (!visible && puffs.length === 0) return;

            // Pass 1 — shadows (drawn first, under everything).
            for (const p of puffs) {
                const lifeA = 1 - p.life / p.maxLife;
                drawShadow(p.x, p.y, p.r * (0.7 + p.z * 0.5), 0.05 * lifeA * (0.4 + p.z * 0.6), 1 - p.z);
            }
            if (center) {
                drawShadow(center.x, center.y + figW * 0.32, figW * 0.52, 0.16, 0.35);
            }

            // Pass 2 — volumetric smoke, far-to-near.
            const sorted = [...puffs].sort((a, b) => a.z - b.z);
            for (const p of sorted) {
                const lifeT = p.life / p.maxLife;
                const ramp = lifeT < 0.12 ? lifeT / 0.12 : 1 - (lifeT - 0.12) / 0.88;
                const alpha = ramp * (0.16 + p.z * 0.34);
                const r = p.r * (0.62 + p.z * 0.62);
                const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
                if (p.warm) {
                    g.addColorStop(0, `rgba(255, 240, 200, ${alpha})`);
                    g.addColorStop(0.4, `rgba(255, 219, 130, ${alpha * 0.55})`);
                    g.addColorStop(1, 'rgba(255, 219, 130, 0)');
                } else {
                    g.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
                    g.addColorStop(0.35, `rgba(214, 238, 250, ${alpha * 0.7})`);
                    g.addColorStop(0.7, `rgba(160, 214, 240, ${alpha * 0.32})`);
                    g.addColorStop(1, 'rgba(160, 214, 240, 0)');
                }
                ctx.fillStyle = g;
                ctx.beginPath();
                ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        raf = requestAnimationFrame(frame);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
        };
    }, [targetSelector, enabled, intensity]);

    if (!enabled) return null;
    return (
        <canvas
            ref={canvasRef}
            aria-hidden
            data-testid="mascot-fx-canvas"
            className="pointer-events-none absolute inset-0"
        />
    );
}
